/**
 * File Type Utility
 * Comprehensive file type detection and validation for documents and images
 */
class FileTypeUtil {
  constructor() {
    this.documentTypes = {
      // Microsoft Office
      'application/pdf': { name: 'PDF Document', extension: 'pdf' },
      'application/msword': { name: 'Word Document', extension: 'doc' },
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { name: 'Word Document', extension: 'docx' },
      'application/vnd.ms-excel': { name: 'Excel Spreadsheet', extension: 'xls' },
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { name: 'Excel Spreadsheet', extension: 'xlsx' },
      'application/vnd.ms-powerpoint': { name: 'PowerPoint Presentation', extension: 'ppt' },
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': { name: 'PowerPoint Presentation', extension: 'pptx' },
      
      // OpenDocument
      'application/vnd.oasis.opendocument.text': { name: 'OpenDocument Text', extension: 'odt' },
      'application/vnd.oasis.opendocument.spreadsheet': { name: 'OpenDocument Spreadsheet', extension: 'ods' },
      'application/vnd.oasis.opendocument.presentation': { name: 'OpenDocument Presentation', extension: 'odp' },
      
      // Apple iWork
      'application/vnd.apple.pages': { name: 'Pages Document', extension: 'pages' },
      'application/vnd.apple.numbers': { name: 'Numbers Spreadsheet', extension: 'numbers' },
      'application/vnd.apple.keynote': { name: 'Keynote Presentation', extension: 'key' },
      
      // Google Workspace
      'application/vnd.google-apps.document': { name: 'Google Docs', extension: 'gdoc' },
      'application/vnd.google-apps.spreadsheet': { name: 'Google Sheets', extension: 'gsheet' },
      'application/vnd.google-apps.presentation': { name: 'Google Slides', extension: 'gslides' },
      
      // Other documents
      'application/rtf': { name: 'Rich Text Format', extension: 'rtf' },
      'text/plain': { name: 'Text File', extension: 'txt' },
      'text/csv': { name: 'CSV File', extension: 'csv' },
      'application/xml': { name: 'XML File', extension: 'xml' },
      'application/json': { name: 'JSON File', extension: 'json' },
      
      // Archives
      'application/zip': { name: 'ZIP Archive', extension: 'zip' },
      'application/x-rar-compressed': { name: 'RAR Archive', extension: 'rar' },
      'application/x-7z-compressed': { name: '7-Zip Archive', extension: '7z' },
      'application/x-tar': { name: 'TAR Archive', extension: 'tar' },
      'application/x-gzip': { name: 'GZIP Archive', extension: 'gz' }
    }

    this.imageTypes = {
      'image/jpeg': { name: 'JPEG Image', extension: 'jpg' },
      'image/jpg': { name: 'JPEG Image', extension: 'jpg' },
      'image/png': { name: 'PNG Image', extension: 'png' },
      'image/gif': { name: 'GIF Image', extension: 'gif' },
      'image/bmp': { name: 'BMP Image', extension: 'bmp' },
      'image/webp': { name: 'WebP Image', extension: 'webp' },
      'image/svg+xml': { name: 'SVG Image', extension: 'svg' },
      'image/tiff': { name: 'TIFF Image', extension: 'tiff' },
      'image/tif': { name: 'TIFF Image', extension: 'tif' },
      'image/x-icon': { name: 'Icon File', extension: 'ico' },
      'image/vnd.microsoft.icon': { name: 'Icon File', extension: 'ico' }
    }

    this.allowedExtensions = [
      // Documents
      'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'rtf', 'txt', 'csv',
      'xml', 'json', 'zip', 'rar', '7z', 'tar', 'gz', 'odt', 'ods', 'odp',
      'pages', 'numbers', 'key', 'gdoc', 'gsheet', 'gslides',
      
      // Images
      'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif', 'ico'
    ]

    this.allowedMimeTypes = [
      ...Object.keys(this.documentTypes),
      ...Object.keys(this.imageTypes)
    ]
  }

  /**
   * Get file type information
   * @param {File} file - File object
   * @returns {Object} File type information
   */
  getFileTypeInfo(file) {
    const extension = this.getFileExtension(file.name)
    const mimeType = file.type
    
    return {
      name: file.name,
      extension: extension,
      mimeType: mimeType,
      isDocument: this.isDocument(file),
      isImage: this.isImage(file),
      typeName: this.getTypeName(mimeType, extension),
      isValid: this.isValidFile(file)
    }
  }

  /**
   * Get file extension
   * @param {string} fileName - File name
   * @returns {string} File extension
   */
  getFileExtension(fileName) {
    return fileName.split('.').pop()?.toLowerCase() || ''
  }

  /**
   * Check if file is a document
   * @param {File} file - File object
   * @returns {boolean}
   */
  isDocument(file) {
    const extension = this.getFileExtension(file.name)
    const mimeType = file.type
    
    // Check if it's a document by MIME type
    if (this.documentTypes[mimeType]) {
      return true
    }
    
    // Check if it's a document by extension
    const documentExtensions = Object.values(this.documentTypes).map(type => type.extension)
    return documentExtensions.includes(extension)
  }

  /**
   * Check if file is an image
   * @param {File} file - File object
   * @returns {boolean}
   */
  isImage(file) {
    const extension = this.getFileExtension(file.name)
    const mimeType = file.type
    
    // Check if it's an image by MIME type
    if (this.imageTypes[mimeType]) {
      return true
    }
    
    // Check if it's an image by extension
    const imageExtensions = Object.values(this.imageTypes).map(type => type.extension)
    return imageExtensions.includes(extension)
  }

  /**
   * Get human-readable type name
   * @param {string} mimeType - MIME type
   * @param {string} extension - File extension
   * @returns {string} Type name
   */
  getTypeName(mimeType, extension) {
    // Try to get name from MIME type first
    if (this.documentTypes[mimeType]) {
      return this.documentTypes[mimeType].name
    }
    if (this.imageTypes[mimeType]) {
      return this.imageTypes[mimeType].name
    }
    
    // Fallback to extension-based detection
    const allTypes = { ...this.documentTypes, ...this.imageTypes }
    for (const [key, value] of Object.entries(allTypes)) {
      if (value.extension === extension) {
        return value.name
      }
    }
    
    return 'Unknown File Type'
  }

  /**
   * Validate file
   * @param {File} file - File object
   * @param {number} maxSize - Maximum file size in bytes
   * @returns {Object} Validation result
   */
  validateFile(file, maxSize = 10 * 1024 * 1024) {
    const info = this.getFileTypeInfo(file)
    
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size exceeds maximum limit of ${maxSize / (1024 * 1024)}MB`
      }
    }

    if (!info.isValid) {
      return {
        valid: false,
        error: `File type not allowed. Allowed: Documents (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, RTF, TXT, CSV, ZIP, RAR) and Images (JPG, PNG, GIF, BMP, WEBP, SVG, TIFF). Detected: ${info.mimeType || 'unknown'} (${info.extension})`
      }
    }

    return { 
      valid: true, 
      info: info 
    }
  }

  /**
   * Check if file is valid
   * @param {File} file - File object
   * @returns {boolean}
   */
  isValidFile(file) {
    const extension = this.getFileExtension(file.name)
    const mimeType = file.type
    
    // Check by MIME type
    if (this.allowedMimeTypes.includes(mimeType)) {
      return true
    }
    
    // Check by extension
    if (this.allowedExtensions.includes(extension)) {
      return true
    }
    
    return false
  }

  /**
   * Get all supported file types
   * @returns {Object} Supported file types
   */
  getSupportedTypes() {
    return {
      documents: this.documentTypes,
      images: this.imageTypes,
      extensions: this.allowedExtensions,
      mimeTypes: this.allowedMimeTypes
    }
  }

  /**
   * Get accept attribute for file input
   * @returns {string} Accept attribute value
   */
  getAcceptAttribute() {
    return this.allowedExtensions.map(ext => `.${ext}`).join(',') + ',' + this.allowedMimeTypes.join(',')
  }
}

// Create singleton instance
const fileTypeUtil = new FileTypeUtil()

export default fileTypeUtil 